import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RootState, StoreDispatch } from "../../store/configure-store"
import { FromDropdown } from "../../cmps/forms/input/form-dropdown"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { httpService } from "../../services/http-service"
import { createEntitySystemMutation } from "../../cmps/util-cmps/graphql-mutations/create-entity-system"
import { updateEntitySystemMutation } from "../../cmps/util-cmps/graphql-mutations/update-entity-system"
import { createSystemMutation } from "../../cmps/util-cmps/graphql-mutations/create-system"
import { AddSystemModal } from "../../cmps/forms/modals/add-system-modal"
import { SystemDetails } from "../../cmps/forms/modals/add-system-modal"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"
import { useCachedFetch } from "../../hooks/use-cached-fetch"

interface EntitySystemFormModalProps {
    companyName: string
    entitySystemToEdit?: any
    existingEntitySystems: any[]
    onSuccess: () => void
    onClose: () => void
}

interface ModalDetails {
    systems: { id: number; name: string }[]
    integrationTypes: { id: string }[]
    entities: { id: number; name: string }[]
}

export const EntitySystemFormModal: React.FC<EntitySystemFormModalProps> = ({
    companyName, entitySystemToEdit, existingEntitySystems, onSuccess, onClose
}) => {
    const isEditMode = !!entitySystemToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isAddSystemModalOpen, setIsAddSystemModalOpen] = useState(false)

    const [form, setForm] = useState(() => isEditMode ? {
        entityId: String(entitySystemToEdit.entity?.id || ''),
        systemId: String(entitySystemToEdit.system?.id || ''),
        integrationType: entitySystemToEdit.integrationType || '',
        isRequiredDaily: String(entitySystemToEdit.isRequiredDaily ?? true),
        isGenerateIpe: String(entitySystemToEdit.isGenerateIpe ?? true),
        forceGenerate: Array.isArray(entitySystemToEdit.forceGenerate) ? entitySystemToEdit.forceGenerate.join(', ') : (entitySystemToEdit.forceGenerate || ''),
    } : {
        entityId: '',
        systemId: '',
        integrationType: '',
        isRequiredDaily: 'true',
        isGenerateIpe: 'true',
        forceGenerate: '',
    })

    const { data: modalDetails, isLoading, hasError } = useCachedFetch<ModalDetails>(
        `entity-system-modal-details:${companyName}`,
        async () => {
            const query = `{
                systems { id name }
                integrationTypes { id }
                entitiesByCompanyName(companyName:"${companyName}") { id name }
            }`
            const result = await httpService.graphql(companyName, query)
            return {
                systems: result.systems || [],
                integrationTypes: result.integrationTypes || [],
                entities: result.entitiesByCompanyName || [],
            }
        }
    )

    const [systemsList, setSystemsList] = useState<{ id: number; name: string }[] | null>(null)
    const systems = systemsList || modalDetails?.systems || []

    useEscapeKey(() => {
        if (!isAddSystemModalOpen) onClose()
    })
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()

    const entityOptions = (modalDetails?.entities || []).map((e) => ({ id: String(e.id), name: e.name }))
    const systemOptions = systems.map((s) => ({ id: String(s.id), name: s.name }))
    const integrationTypeOptions = (modalDetails?.integrationTypes || []).map((t) => ({ id: t.id, name: t.id }))
    const boolOptions = [{ id: 'true', name: 'True' }, { id: 'false', name: 'False' }]

    const onChangeDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const validate = (): string | null => {
        if (!form.entityId) return 'Entity is required'
        if (!form.systemId) return 'System is required'
        if (!form.integrationType) return 'Integration Type is required'

        if (!isEditMode) {
            const duplicate = existingEntitySystems.some((es: any) =>
                String(es.entity?.id) === form.entityId && String(es.system?.id) === form.systemId
            )
            if (duplicate) return 'This entity + system combination already exists'
        }

        return null
    }

    const onAddSystem = async (systemDetails: SystemDetails) => {
        const { name, abbreviation, type, logoUrl } = systemDetails
        const query = createSystemMutation(name, abbreviation, type, logoUrl)
        const res = await httpService.graphql(companyName, query)
        const newSystem = res.createSystem.system

        const updated = [...systems, { id: newSystem.id, name: newSystem.name }].sort((a, b) => a.name.localeCompare(b.name))
        setSystemsList(updated)
        setForm(prev => ({ ...prev, systemId: String(newSystem.id) }))
        setIsAddSystemModalOpen(false)
    }

    const onSubmit = async () => {
        const error = validate()
        if (error) {
            const msg: UserMsg = { type: 'danger', txt: error }
            dispatch(setUserMsg({ msg }))
            return
        }

        setIsSubmitting(true)
        try {
            if (isEditMode) {
                const query = updateEntitySystemMutation()
                const forceGenerateList = form.forceGenerate.trim()
                    ? form.forceGenerate.split(',').map((s: string) => s.trim()).filter(Boolean)
                    : null
                const variables = {
                    entitySystemId: Number(entitySystemToEdit.id),
                    integrationType: form.integrationType,
                    isRequiredDaily: form.isRequiredDaily === 'true',
                    isGenerateIpe: form.isGenerateIpe === 'true',
                    forceGenerate: forceGenerateList ? JSON.stringify(forceGenerateList) : null,
                }
                await httpService.graphql(companyName, { query, variables })
                const msg: UserMsg = { type: 'success', txt: 'Entity system updated successfully' }
                dispatch(setUserMsg({ msg }))
            } else {
                const createForceGenerate = form.forceGenerate.trim()
                    ? JSON.stringify(form.forceGenerate.split(',').map((s: string) => s.trim()).filter(Boolean))
                    : null
                const query = createEntitySystemMutation(
                    Number(form.entityId),
                    Number(form.systemId),
                    form.integrationType,
                    form.isRequiredDaily === 'true',
                    form.isGenerateIpe === 'true',
                    createForceGenerate
                )
                await httpService.graphql(companyName, query)
                const msg: UserMsg = { type: 'success', txt: 'Entity system created successfully' }
                dispatch(setUserMsg({ msg }))
            }
            onSuccess()
        } catch {
            const msg: UserMsg = { type: 'danger', txt: isEditMode ? 'Failed updating entity system' : 'Failed creating entity system' }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit Entity System' : 'Create Entity System'

    if (isLoading || hasError) {
        return <div ref={trapRef}>
            <section className="entity-system-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
                <header>
                    <h2>{modalTitle}</h2>
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    {isLoading && <Spinner />}
                    {hasError && <p>Failed to load form data</p>}
                </div>
            </section>
            <Backlog zIndex={199} onCloseBacklog={onClose} />
        </div>
    }

    const entityName = isEditMode
        ? entitySystemToEdit.entity?.name || ''
        : entityOptions.find(e => e.id === form.entityId)?.name || ''
    const systemName = isEditMode
        ? entitySystemToEdit.system?.name || ''
        : systemOptions.find(s => s.id === form.systemId)?.name || ''

    return <>
    <div ref={trapRef}>
        <section className="entity-system-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">Entity & System</div>
                    <div className="form-row">
                        {isEditMode ? (
                            <div className="field">
                                <label>Entity</label>
                                <input type="text" value={entityName} disabled />
                            </div>
                        ) : (
                            <div className="field">
                                <FromDropdown
                                    keyName="entityId"
                                    label="Entity"
                                    options={entityOptions}
                                    value={form.entityId}
                                    onSelectValue={onChangeDropdown}
                                />
                            </div>
                        )}
                        {isEditMode ? (
                            <div className="field">
                                <label>System</label>
                                <input type="text" value={systemName} disabled />
                            </div>
                        ) : (
                            <div className="field-with-add">
                                <FromDropdown
                                    keyName="systemId"
                                    label="System"
                                    options={systemOptions}
                                    value={form.systemId}
                                    onSelectValue={onChangeDropdown}
                                    onAddBtn={() => setIsAddSystemModalOpen(true)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Configuration</div>
                    <div className="form-row">
                        <div className="field">
                            <FromDropdown
                                keyName="integrationType"
                                label="Integration Type"
                                options={integrationTypeOptions}
                                value={form.integrationType}
                                onSelectValue={onChangeDropdown}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="isRequiredDaily"
                                label="Is Required Daily"
                                options={boolOptions}
                                value={form.isRequiredDaily}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={true}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="isGenerateIpe"
                                label="Generate IPE"
                                options={boolOptions}
                                value={form.isGenerateIpe}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={true}
                            />
                        </div>
                        <div className="field span-2">
                            <FormInputText
                                keyName="forceGenerate"
                                label="Force Generate (Audit Control IDs, comma-separated)"
                                isRequired={false}
                                value={form.forceGenerate}
                                onChange={onChangeText}
                                placeholder="[]"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>

    {isAddSystemModalOpen && <AddSystemModal
        onAddSystem={onAddSystem}
        onCloseModal={() => setIsAddSystemModalOpen(false)}
        triggerMissingRequiredFields={() => {
            const msg: UserMsg = { type: 'danger', txt: 'Please fill all required fields' }
            dispatch(setUserMsg({ msg }))
        }}
        triggerErrorMsg={(txt: string) => {
            const msg: UserMsg = { type: 'danger', txt }
            dispatch(setUserMsg({ msg }))
        }}
    />}
    </>
}
